import React from 'react';
import classNames from 'classnames';

import { DynamicMediaItem } from '@uncinc/react-drupal-core';
import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import { Container } from '@uncinc/uncinc-react-kitchen-sink';
import {
  Text,
} from '../../../components';
import splitReadMore from '../../../helpers/splitReadMore';

import { ParagraphTextMedia as Type } from '../../../types/uncinc_profile/paragraphs';
import type { HeadingTag } from '../../../types';

import './style.scss';

interface ParagraphTextMediaProps {
  entity: ImmutableEntity<Type>;
  headingTag?: HeadingTag;
  textColClass?: string;
  mediaColClass?: string;
}

const ParagraphTextMedia: React.FC<ParagraphTextMediaProps> = ({
  entity,
  headingTag,
  textColClass,
  mediaColClass,
}) => {
  const paragraphTextMedia = entity.toJS();

  const title = paragraphTextMedia.field_title[0]?.value;
  const bodyRaw = paragraphTextMedia.field_body[0]?.processed || '';
  const { intro, readMore } = splitReadMore(bodyRaw);
  const media = paragraphTextMedia.field_media_item[0];
  const linkField = paragraphTextMedia.field_link[0];
  const link = linkField?.uri?.replace(/^internal:/, '') ?? undefined;
  const linkText = linkField?.title ?? undefined;

  let posterImage;
  if (media.bundle === 'video') {
    posterImage = media.entity.field_poster_image[0]?.url;
  }

  const isLargeMedia = paragraphTextMedia.field_large_media[0]?.value;
  const useOriginalRatio = paragraphTextMedia.field_original_ratio[0]?.value;

  const alignment = paragraphTextMedia.field_alignment[0]?.value;
  const isHorizontal = alignment === 'left' || alignment === 'right';

  const rowClass = classNames('row', {
    'align-items-center': isHorizontal,
    'ParagraphTextMedia__row--horizontal': isHorizontal,
  });

  const aspectRatio = isHorizontal ? '4_3' : '16_9';

  const textContainer = classNames('ParagraphTextMedia__text', {
    [textColClass || 'col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2']: !isHorizontal,
    'col-12 col-md-6': isHorizontal,
    'order-md-2 ParagraphTextMedia__text--right': alignment === 'right',
  });

  const mediaContainer = classNames('ParagraphTextMedia__media', {
    'col-12': !isHorizontal,
    [mediaColClass || 'col-md-10 offset-md-1 col-lg-8 offset-lg-2']: !isLargeMedia && !isHorizontal,
    'col-12 col-md-6': isHorizontal,
    'order-md-1': alignment === 'right',
  });

  return (
    <div className="Paragraph ParagraphTextMedia">
      <Container>
        <div className={rowClass}>
          {intro && (
            <div className={textContainer}>
              <Text
                title={title}
                text={intro}
                readMore={readMore}
                readMoreHeight={60}
                link={link}
                linkText={linkText}
                headingTag={headingTag}
              />
            </div>
          )}
          <div className={mediaContainer}>
            <DynamicMediaItem
              media={media}
              imageAspectRatio={useOriginalRatio ? 'original' : aspectRatio}
              posterImage={posterImage}
            />
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ParagraphTextMedia;
